// Firefly fly between floating tab ↔ sidebar dock slot, then persist mode.
import { useEffect, useRef, useState } from "react";
import { FireflyTourTravelFirefly } from "@/components/activity/firefly/FireflyTourTravelFirefly";
import { useFireflyTourTravel } from "@/components/activity/firefly/useFireflyTourTravel";
import { setFireflyDisplayMode } from "@/lib/firefly/fireflyDisplayMode";
import {
  FIREFLY_SIDEBAR_DOCK_REQUEST_EVENT,
  resolveFireflyDockTravelPoints,
  setFireflySidebarDockTravelActive,
  type FireflySidebarDockRequestDetail,
} from "@/lib/firefly/fireflySidebarDockTravel";

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function FireflySidebarDockTravelHost() {
  const reducedMotion = prefersReducedMotion();
  const { traveling, position, flyTo } = useFireflyTourTravel(reducedMotion);
  const busyRef = useRef(false);
  const [, setTick] = useState(0);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onRequest = (e: Event) => {
      const detail = (e as CustomEvent<FireflySidebarDockRequestDetail>).detail;
      const animate = detail?.animate !== false && !prefersReducedMotion();
      const direction = detail?.direction ?? "to-sidebar";
      void (async () => {
        if (busyRef.current) return;
        busyRef.current = true;
        try {
          const points = resolveFireflyDockTravelPoints(direction);
          setFireflySidebarDockTravelActive(true);
          setTick((n) => n + 1);
          if (animate && points) {
            await flyTo(points.from, points.to);
          }
          setFireflyDisplayMode(direction === "to-sidebar" ? "docked" : "floating");
        } finally {
          setFireflySidebarDockTravelActive(false);
          busyRef.current = false;
          setTick((n) => n + 1);
        }
      })();
    };
    window.addEventListener(FIREFLY_SIDEBAR_DOCK_REQUEST_EVENT, onRequest);
    return () => window.removeEventListener(FIREFLY_SIDEBAR_DOCK_REQUEST_EVENT, onRequest);
  }, [flyTo]);

  if (!traveling) return null;
  return (
    <FireflyTourTravelFirefly
      viewportX={position.x}
      viewportY={position.y}
      intent="guidance"
    />
  );
}
